import React, { useEffect, useContext, useCallback, useState } from "react";
import { onAuthStateChanged, User } from "firebase/auth";
import { auth } from "./firebase/config";

import Dashboard from "./Components/Dashboard/Dashboard";
import LandingPage from "./Components/LandingPage/LandingPage";
import Context from "./Context";
import { apiFetch, setCurrentUserId } from "./utils/api";

import styles from "./App.module.scss";
import { CraCheckReportProduct } from "plaid";

const App = () => {
  const { dispatch } = useContext(Context);
  
  const [currentUser, setCurrentUser] = useState<User | null>(null);
  const [authLoading, setAuthLoading] = useState(true);

  const checkBackendConnection = useCallback(async () => {
    try {
      const response = await apiFetch("/api/connection/status");
      if (!response.ok) {
        dispatch({ type: "SET_STATE", state: { linkSuccess: false, accessToken: null } });
        return false;
      }
      const data = await response.json();
      if (!data.connected) {
        // Backend lost the token (restart), try to restore from Context if available
        console.log('⚠️ Backend disconnected, connection will be restored by Dashboard');
        return false;
      }
      return true;
    } catch (error) {
      console.error('Failed to check backend connection:', error);
      return false;
    }
  }, [dispatch]);

  const getInfo = useCallback(async () => {
    const response = await apiFetch("/api/info", { method: "POST" });
    if (!response.ok) {
      dispatch({ type: "SET_STATE", state: { backend: false } });
      return { paymentInitiation: false };
    }
    const data = await response.json();
    const paymentInitiation: boolean =
      data.products.includes("payment_initiation");
    const craEnumValues = Object.values(CraCheckReportProduct);
    const isUserTokenFlow: boolean = data.products.some(
      (product: CraCheckReportProduct) => craEnumValues.includes(product)
    );
    const isCraProductsExclusively: boolean = data.products.every(
      (product: CraCheckReportProduct) => craEnumValues.includes(product)
    );
    dispatch({
      type: "SET_STATE",
      state: {
        products: data.products,
        isPaymentInitiation: paymentInitiation,
        isCraProductsExclusively: isCraProductsExclusively,
        isUserTokenFlow: isUserTokenFlow,
      },
    });
    return { paymentInitiation, isUserTokenFlow };
  }, [dispatch]);

  const generateUserToken = useCallback(async () => {
    const response = await fetch("api/create_user_token", { method: "POST" });
    if (!response.ok) {
      dispatch({ type: "SET_STATE", state: { userToken: null } });
      return;
    }
    const data = await response.json();
    if (data) {
      if (data.error != null) {
        dispatch({
          type: "SET_STATE",
          state: {
            linkToken: null,
            linkTokenError: data.error,
          },
        });
        return;
      }
      dispatch({ type: "SET_STATE", state: { userToken: data.user_token } });
      return data.user_token;
    }
  }, [dispatch]);

  const generateToken = useCallback(
    async (isPaymentInitiation) => {
      // TODO: Only call this when user clicks "Connect Bank" button
      // Not on app startup to avoid unnecessary API calls
      const path = isPaymentInitiation
        ? "/api/create_link_token_for_payment"
        : "/api/create_link_token";
      const response = await fetch(path, {
        method: "POST",
        // TODO: Add Firebase Auth token to headers
        // headers: {
        //   'Authorization': `Bearer ${firebaseAuthToken}`
        // }
      });
      if (!response.ok) {
        dispatch({ type: "SET_STATE", state: { linkToken: null } });
        return;
      }
      const data = await response.json();
      if (data) {
        if (data.error != null) {
          dispatch({
            type: "SET_STATE",
            state: {
              linkToken: null,
              linkTokenError: data.error,
            },
          });
          return;
        }
        dispatch({ type: "SET_STATE", state: { linkToken: data.link_token } });
      }
      // Save the link_token to be used later in the Oauth flow.
      localStorage.setItem("link_token", data.link_token);
    },
    [dispatch]
  );

  useEffect(() => {
    // Listen for auth state changes
    const unsubscribe = onAuthStateChanged(auth, (user: User | null) => {
      setCurrentUser(user);
      setAuthLoading(false);
      
      // CRITICAL: Set the user ID for all API requests
      // This ensures each user's data is isolated on the backend
      if (user) {
        setCurrentUserId(user.uid);
        console.log(`✅ User authenticated: ${user.email} (${user.uid.substring(0, 8)}...)`);
      } else {
        setCurrentUserId(null);
        console.log('❌ User signed out, cleared user ID');
      }
    });

    // Cleanup subscription
    return () => unsubscribe();
  }, []);

  useEffect(() => {
    const init = async () => {
      // Only initialize if user is authenticated
      if (!currentUser) return;
      
      // Check if backend still has the access token
      await checkBackendConnection();
      
      const { paymentInitiation, isUserTokenFlow } = await getInfo();
      
      // Handle OAuth redirect
      if (window.location.href.includes("?oauth_state_id=")) {
        dispatch({
          type: "SET_STATE",
          state: {
            linkToken: localStorage.getItem("link_token"),
          },
        });
        return;
      }

      // TODO: Don't generate token on startup in production
      // Move this to user action (e.g., "Connect Bank" button click)
      if (isUserTokenFlow) {
        await generateUserToken();
      }
      generateToken(paymentInitiation);
    };
    
    if (!authLoading) {
      init();
    }
  }, [currentUser, authLoading, dispatch, generateToken, generateUserToken, getInfo, checkBackendConnection]);

  return (
    <div className={styles.App}>
      {authLoading ? (
        <div className={styles.container}>
          <div className={styles.loading}>Loading...</div>
        </div>
      ) : !currentUser ? (
        // Show landing page with authentication
        <LandingPage onAuthenticated={() => {/* User will be auto-detected by auth state listener */}} />
      ) : (
        // Show new Dashboard for authenticated users
        <Dashboard />
      )}
    </div>
  );
};

export default App;
